import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const cache = new Map();
const keyOf = (path, params) => `${path}?${JSON.stringify(params || {})}`;

export const primeCache = (path, data, params) => { if (!cache.has(keyOf(path, params))) cache.set(keyOf(path, params), data); };
export const clearCache = () => cache.clear();

export function useCachedGet(path, params, { enabled = true } = {}) {
  const key = keyOf(path, params);
  const [state, setState] = useState(() => ({ key, data: cache.get(key) ?? null, error: false }));
  const keyRef = useRef(key);
  keyRef.current = key;
  const load = useCallback(async () => {
    const k = key;
    try {
      const { data } = await api.get(path, { params: JSON.parse(k.slice(k.indexOf("?") + 1)) });
      cache.set(k, data);
      if (keyRef.current === k) setState({ key: k, data, error: false });
    } catch {
      if (keyRef.current === k) setState((s) => ({ key: k, data: s.key === k ? s.data : null, error: true }));
    }
  }, [path, key]);
  useEffect(() => { if (enabled) load(); }, [load, enabled]);
  // Also refresh when the user comes back to the tab.
  useEffect(() => {
    if (!enabled) return undefined;
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("bazaro-lang-change", load);
    return () => { document.removeEventListener("visibilitychange", onFocus); window.removeEventListener("bazaro-lang-change", load); };
  }, [load, enabled]);
  const current = state.key === key ? state : { key, data: cache.get(key) ?? null, error: false };
  const setData = useCallback((fn) => setState((s) => {
    const data = typeof fn === "function" ? fn(s.data) : fn;
    cache.set(s.key, data);
    return { ...s, data };
  }), []);
  return { data: current.data, loading: current.data === null && !current.error, error: current.error && current.data === null, reload: load, setData };
}
